'use client';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import { useEffect, useState, useTransition } from 'react';
import { useForm } from 'react-hook-form';
import { AlertTriangle, CircleCheckBig, Eye, EyeOff } from 'lucide-react';
import { takeSessionEndedReason } from '@/utils/utils';
import { type FormAuthenticationSchema, formAuthenticationSchema } from '@/lib/form-schema';
import { fetchUser } from '@/services/userService';
import { toast } from '@/components/ui/use-toast';
import { useDispatch } from 'react-redux';
import { loadUserRequest, loadUserSuccess, loadUserFail } from '@/redux/reducers/user';
import { Loader2 } from 'lucide-react';

export default function UserAuthForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, startTransition] = useTransition();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [endedReason, setEndedReason] = useState('');
  const router = useRouter();

  useEffect(() => setEndedReason(takeSessionEndedReason()), []);
  const dispatch = useDispatch();
  const defaultValues = {
    username: '',
    password: ''
  };
  const form = useForm<FormAuthenticationSchema>({
    resolver: zodResolver(formAuthenticationSchema),
    defaultValues
  });
  const { watch } = form;
  const passwordValue = watch('password');

  const storeCredentialBestEffort = async (username: string, password: string) => {
    if (typeof window === 'undefined' || !rememberMe) {
      return;
    }

    if (
      !window.isSecureContext ||
      typeof navigator.credentials === 'undefined' ||
      typeof navigator.credentials.store !== 'function' ||
      typeof PasswordCredential === 'undefined'
    ) {
      return;
    }

    try {
      const credential = new PasswordCredential({
        id: username,
        password
      });
      await navigator.credentials.store(credential);
    } catch (error) {
      console.warn('Browser could not store login credential for autofill.', error);
    }
  };

  const onSubmit = async (data: FormAuthenticationSchema) => {
    setIsSubmitting(true);
    dispatch(loadUserRequest());
    try {
      const fetchUserLogin = await fetchUser(data.username, data.password);
      const userData = fetchUserLogin.data;
      const user = {
        id: userData.id,
        user_name: userData.user_name,
        full_name: userData.full_name,
        phone: userData.phone,
        email: userData.email,
        last_seen: userData.last_seen,
        is_admin: userData.is_admin
      };
      localStorage.setItem('token', userData.token);
      localStorage.setItem('refresh_token', userData.refresh_token);
      localStorage.setItem('user', JSON.stringify(user));
      void storeCredentialBestEffort(data.username, data.password);
      dispatch(loadUserSuccess(user));
      toast({
        title: 'Đăng nhập thành công',
        description: (
          <div className="flex items-center space-x-2 mt-2 w-[340px] rounded-md bg-black p-4 animate-fade-in">
            <pre>
              <p className="text-white text-sm font-medium">Tài khoản: {user.full_name}</p>
            </pre>
            <CircleCheckBig className="w-6 h-6 animate-pulse text-green-500" />
          </div>
        ),
      });
      startTransition(() => {
        router.push('/dashboard');
      });
    } catch (error: unknown) {
      showErrorToast(error);
      dispatch(loadUserFail("Đăng nhập thất bại!" + (error instanceof Error ? error.message : '')));
    } finally {
      setIsSubmitting(false);
    }
  };

  const showErrorToast = (error: unknown) => {
    const message = error instanceof Error ? error.message : '';
    toast({
      variant: 'destructive',
      title: 'Uh oh! Có một số lỗi',
      description: message ? 'Vui lòng đợi vài phút rồi đăng nhập lại \n' + message : 'Có lỗi xảy ra, vui lòng thử lại sau!',
    });
  };

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="w-full space-y-2" autoComplete="on">
          {endedReason && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{endedReason}</span>
            </div>
          )}
          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tài khoản</FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder="Nhập tài khoản..."
                    disabled={loading}
                    autoComplete="username"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Mật khẩu</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Nhập mật khẩu..."
                      disabled={loading}
                      autoComplete="current-password"
                      {...field}
                    />
                    {passwordValue?.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        className="absolute right-2 top-2"
                      >
                        {showPassword ? <Eye className='w-5 h-5 text-gray-800 opacity-75' /> : <EyeOff className='w-5 h-5 text-gray-800 opacity-75' />}
                      </button>
                    )}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex items-center gap-2 py-1">
            <Checkbox
              id="remember-me"
              checked={rememberMe}
              onCheckedChange={(checked) => setRememberMe(checked === true)}
              disabled={loading || isSubmitting}
            />
            <label htmlFor="remember-me" className="text-sm text-muted-foreground">
              Ghi nhớ đăng nhập
            </label>
          </div>
          <Button disabled={isSubmitting || loading} className="ml-auto w-full" type="submit">
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Đang chuyển hướng...
              </>
            ) : (
              'Đăng nhập'
            )}
          </Button>
        </form>
      </Form>
    </>
  );
}
